/* One row on the officer's SYSTEM FAILURES line.
 *
 * Ale ruling 2026-08-09 — the split is WITHIN the verb. The officer gets
 * the cheap recovery and nothing else:
 *
 *   document_stuck · dead_letter_review   one-click WHOLE-GROUP Retry
 *   unroutable                            no verb at all — "admin only"
 *
 * Diagnosis, the per-leg PARTIAL retry, discard and the raw payload stay
 * on the admin view (OpsCard). This row deliberately shows no legs, no
 * error blob and no payload.
 *
 * Honest reporting binds here (never-dead-end, Ale 2026-07-22): a retry
 * that dead-letters AGAIN says so ON THE ROW and the row stays
 * actionable, because the task stays open server-side. Never a silent
 * requeue.
 *
 * W6 SEAM: this is the component the workbench's system-failures line
 * renders per row. It owns the verb and its outcome reporting; W6 owns
 * the line, its heading and its placement. */

import { useState } from 'react'
import { opsErrorText, outcomeLine, retryWholeGroup } from './api'
import type { OpsFailureRow as RowData } from './types'

interface Props {
  row: RowData
  /** Called once the row settles, so the line can refresh its counts. */
  onSettled?: () => void
  /** Optional: let the officer hand a row to an admin. W6 owns routing,
   *  so without it the row simply reads "admin only". */
  onOpenAdminView?: (taskId: string) => void
}

export default function SystemFailureRow({ row, onSettled, onOpenAdminView }: Props) {
  const [busy, setBusy] = useState(false)
  const [outcome, setOutcome] = useState<{ text: string; bad: boolean } | null>(null)
  const [spent, setSpent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canRetry = row.actions.includes('retry') && !row.retry_blocked_reason

  async function retry() {
    setBusy(true)
    setError(null)
    try {
      const result = await retryWholeGroup(row.task_id)
      const line = outcomeLine(result)
      setOutcome({ text: line.text, bad: line.bad })
      // spent=false means the re-run dead-lettered again: the task is
      // still open, so the row must stay live and retryable.
      if (line.spent) setSpent(true)
      onSettled?.()
    } catch (err) {
      setError(opsErrorText(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className={`sfrow${spent ? ' spent' : ''}`}
      data-testid="ops-failure-row"
      data-kind={row.kind}
      data-task-id={row.task_id}
    >
      <span className="k" data-testid="ops-failure-tag">
        {row.tag}
      </span>
      <span className="nm">{row.anchor_label}</span>
      {row.fund_name ? <span className="fund">{row.fund_name}</span> : null}
      <span className={`age${row.age_days > 30 ? ' warn' : ''}`}>
        {row.age_days === 0 ? 'today' : `${row.age_days} d`}
      </span>

      {/* The task's OWN payload as labelled fields — never a sentence.
          document_stuck: document · pipeline step · retry count · event id.
          dead_letter_review: group · pipeline step · retry count · failing
          leg. unroutable: task type · target · priority · why it is here. */}
      {row.fields.length > 0 ? (
        <dl className="sffields" data-testid="ops-failure-fields">
          {row.fields.map((f) => (
            <div key={`${f.key}-${f.value}`}>
              <dt>{f.key}</dt>
              <dd>
                {f.value}
                {f.note ? <small>{f.note}</small> : null}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}

      {/* the failing constraint, verbatim */}
      {row.error_text ? (
        <code className="sferr" data-testid="ops-failure-error-text">
          {row.error_text}
        </code>
      ) : null}

      {canRetry ? (
        <button
          type="button"
          className="btn fail"
          disabled={busy || spent}
          onClick={retry}
          data-testid="ops-failure-retry"
        >
          {busy ? 'Retrying…' : 'Retry'}
        </button>
      ) : (
        <span className="adminonly" data-testid="ops-failure-admin-only">
          admin only
        </span>
      )}

      {onOpenAdminView ? (
        <button
          type="button"
          className="btn ghost"
          onClick={() => onOpenAdminView(row.task_id)}
          data-testid="ops-failure-open-admin"
        >
          Open in admin →
        </button>
      ) : null}

      {row.retry_blocked_reason ? (
        <span className="why" data-testid="ops-failure-blocked">
          {row.retry_blocked_reason}
        </span>
      ) : null}

      {outcome ? (
        <span
          className={`outcome${outcome.bad ? ' bad' : ''}`}
          data-testid="ops-failure-outcome"
          role="status"
        >
          {outcome.bad ? '✗' : '✓'} {outcome.text}
        </span>
      ) : null}

      {error ? (
        <span className="outcome bad" data-testid="ops-failure-error">
          {error}
        </span>
      ) : null}
    </div>
  )
}
